/**
 * Unified attach button: single button that opens a file picker accepting
 * both images and documents. Selected files are automatically routed:
 *   - image/* → onAddImages (data URL strings)
 *   - .txt/.md/.csv/.json/.pdf/.docx → onAddFiles (AttachedFile objects)
 *
 * Thumbnails for each type are still rendered by AttachImageThumbnails
 * and AttachFileThumbnails from their respective modules.
 */

import React, { useRef, useCallback } from "react";

import { readFilesAsDataUrls } from "./AttachImage";
import { readFilesAsBase64, ALLOWED_FILE_ACCEPT, type AttachedFile } from "./AttachFile";

const UNIFIED_ACCEPT = `image/*,${ALLOWED_FILE_ACCEPT}`;

export interface AttachButtonProps {
  onAddImages: (urls: string[]) => void;
  onAddFiles: (files: AttachedFile[]) => void;
  onFileTooLarge?: (filenames: string[]) => void;
  disabled?: boolean;
  buttonClassName?: string;
  title?: string;
}

export const AttachButton: React.FC<AttachButtonProps> = ({
  onAddImages,
  onAddFiles,
  onFileTooLarge,
  disabled = false,
  buttonClassName = "",
  title = "Add image / file",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList?.length) {
        return;
      }

      const imageFiles: File[] = [];
      const docFiles: File[] = [];

      for (const f of Array.from(fileList)) {
        if (f.type.startsWith("image/")) {
          imageFiles.push(f);
        } else {
          docFiles.push(f);
        }
      }

      const allRejected: string[] = [];
      const collectRejected = (names: string[]) => allRejected.push(...names);

      if (imageFiles.length > 0) {
        const dt = new DataTransfer();
        imageFiles.forEach((f) => dt.items.add(f));
        const urls = await readFilesAsDataUrls(dt.files, collectRejected);
        if (urls.length) {
          onAddImages(urls);
        }
      }

      if (docFiles.length > 0) {
        const dt = new DataTransfer();
        docFiles.forEach((f) => dt.items.add(f));
        const files = await readFilesAsBase64(dt.files, collectRejected);
        if (files.length) {
          onAddFiles(files);
        }
      }

      if (allRejected.length && onFileTooLarge) {
        onFileTooLarge(allRejected);
      }

      e.target.value = "";
    },
    [onAddImages, onAddFiles, onFileTooLarge],
  );

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        title={title}
        aria-label={title}
      >
        <span className="material-symbols-outlined">add_photo_alternate</span>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={UNIFIED_ACCEPT}
        multiple
        className="attach-image__file-input"
        onChange={handleSelect}
      />
    </>
  );
};
